import { Component, Directive, Inject, OnDestroy, OnInit } from '@angular/core';
import { TokenService } from 'services/token/token.service';
import { TokenStorageService } from 'services/token/token-storage.service';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoginFormComponent } from 'pages/entry/login-form/login-form.component';
import { Meta, Title } from '@angular/platform-browser';
import { LOGIN_ERRORS, SIGN_IN_TYPES } from 'pages/entry/entry.helpers';
import { LOGIN_ENTITY_NAME } from 'pages/entry/login/login.component.constants';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LOGIN_STATUSES } from 'constants/auth.constants';
import _get from 'lodash-es/get';
import { LoginProviderRedirectResponse } from 'services/login-provider/login-provider.interface';
import { ActivatedRoute, NavigationEnd, Router, Scroll } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { delay, filter, map } from 'rxjs/operators';
import { DomainService } from 'services/domain/domain.service';

@UntilDestroy()
@Directive()
export abstract class AbstractLoginComponent {
  error: string;
  loginType: SIGN_IN_TYPES = SIGN_IN_TYPES.DEFAULT;
  isSubmitted = false;
  readonly entityNames = LOGIN_ENTITY_NAME;

  get isDefaultLoginType(): boolean {
    return this.loginType === SIGN_IN_TYPES.DEFAULT;
  }

  get isExternalLoginType(): boolean {
    return [
      SIGN_IN_TYPES.GOOGLE,
      SIGN_IN_TYPES.ONE_LOGIN,
      SIGN_IN_TYPES.OKTA,
      SIGN_IN_TYPES.AZURE,
      SIGN_IN_TYPES.ENTRA,
      SIGN_IN_TYPES.PING,
    ].includes(this.loginType);
  }

  protected handleError(loginError) {
    const codeName = _get(loginError, 'body.code_name');
    if (codeName === LOGIN_STATUSES.MEMBER_CLICKS_ERROR) {
      this.error = LOGIN_ERRORS.MEMBER_CLICKS_ERROR;
    } else if (codeName === LOGIN_STATUSES.MEMBER_CLICKS_VALIDATION_ERROR) {
      this.error = LOGIN_ERRORS.MEMBER_CLICKS_VALIDATION_ERROR;
    } else if (this.isDefaultLoginType) {
      this.error = LOGIN_ERRORS.INCORRECT_INFO;
    } else if (this.isExternalLoginType) {
      this.error = LOGIN_ERRORS.INCORRECT_ACCOUNT;
    } else {
      this.error = LOGIN_ERRORS.INCORRECT_INFO_USERNAME;
    }
  }
}

// for debug add '', 'localhost' to this list
const ALLOWED_REFERRERS = ['linkedin', 'capterra', 'google', 'bing'];
const POPUP_TIMEOUT = 1500;

@UntilDestroy()
@Component({
  selector: 'cm-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent extends AbstractLoginComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>();
  isPopupShown = false;
  FIRST_TIME_ON_L0GIN_PAGE = 1;
  subdomainNames = ['ncblg', 'tadgrants', 'pebc'];

  constructor(
    private tokenService: TokenService,
    private tokenStorage: TokenStorageService,
    private store: Store<any>,
    private modalService: NgbModal,
    private domainService: DomainService,
    private titleService: Title,
    private meta: Meta,
    private activeRoute: ActivatedRoute,
    @Inject(DOCUMENT) private _document: Document,
    private router: Router,
  ) {
    super();
    this.router.events
      .pipe(
        filter(
          (event: any) =>
            event instanceof NavigationEnd || (event instanceof Scroll && event.routerEvent instanceof NavigationEnd),
        ),
        map((event: any) => (event instanceof Scroll ? event.routerEvent : event)),
        delay(POPUP_TIMEOUT),
        untilDestroyed(this),
      )
      .subscribe((event: NavigationEnd) => {
        this.isPopupShown =
          event.id === this.FIRST_TIME_ON_L0GIN_PAGE &&
          !this.subdomainNames.includes(this.domainService.getSubdomainForLogin());
      });
  }

  ngOnInit() {
    this.tokenService
      .getLoginType()
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        this.loginType = result;

        const queryParams = this.activeRoute.snapshot.queryParams;
        if (queryParams) {
          const { accessToken, type, organizationId, error } = queryParams as LoginProviderRedirectResponse;
          if ([SIGN_IN_TYPES.ONE_LOGIN, SIGN_IN_TYPES.OKTA, SIGN_IN_TYPES.AZURE, SIGN_IN_TYPES.ENTRA, SIGN_IN_TYPES.PING].includes(type)) {
            this.loginType = type;
            if (error === LOGIN_ERRORS.ACCESS_DENIED) {
              this.handleError(null);
            } else if (accessToken) {
              this.onLoginToken(accessToken, type, Number(organizationId));
            }
          }
        }
      });

    this.titleService.setTitle('Certemy - Customer Login');
    this.meta.addTag({
      name: 'description',
      content: 'Login page for all Certemy users including professionals and board staff and managers.',
    });
  }

  ngOnDestroy() {
    this.titleService.setTitle('Certemy');
    this.meta.removeTag('name = description');
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private setIsPopupShown() {
    const referrer = this._document.referrer;
    const host = referrer ? new URL(referrer).host : '';
    const isShowPopupAllowed = ALLOWED_REFERRERS.some((ref: string) => host.includes(ref));
    if (isShowPopupAllowed) {
      setTimeout(() => {
        this.isPopupShown = true;
      }, POPUP_TIMEOUT);
    }
  }

  onSubmit(loginForm: LoginFormComponent): void {
    this.isSubmitted = true;
    const formData = loginForm.getFormData();
    if (!!formData) {
      const { username, password } = formData;
      this.tokenService.getUserLogin({ username, password }).subscribe(
        (errorMessage) => (this.error = errorMessage),
        (loginError) => this.handleError(loginError),
      );
    }
  }

  onTokenLogin(response: any): void {
    const { credential, type } = response;
    this.loginType = type;
    this.onLoginToken(credential, type);
  }

  onLoginToken(token: string, type: SIGN_IN_TYPES, organizationId?: number) {
    this.tokenService.getUserLoginByToken(token, type, organizationId).subscribe(
      (errorMessage) => (this.error = errorMessage),
      (loginError) => this.handleError(loginError),
    );
  }

  handleError(loginError) {
    // console.log(loginError);
    super.handleError(loginError);
  }
}
